/**
 * Strip internal annotations from draft legal markdown before rendering.
 *
 * `privacy-policy.md` and `terms-of-service.md` are NOT documentation — they are imported `?raw`
 * and RENDERED as the public `/privacy-policy` and `/terms-of-service` pages. Everything left in
 * them ships to users, so anything written for an internal reader has to come out at render time.
 *
 * Three classes are removed:
 *   1. `[FLAG: ...]` callouts — lawyer-review annotations.
 *   2. The `> **Document status: DRAFT.**` blockquote and its continuation lines.
 *   3. (s184, D-184.12) The internal VERSION/CHANGELOG header — the `*Version ...*` and
 *      `*Drafted: ...*` italic lines under the title. These were rendering publicly, leaking
 *      session numbers, D-references, internal audit filenames and version history onto the live
 *      pages. Measured on the live page before the fix: 1,603 characters of internal header,
 *      4 `session NN` references, 1 `D-NNN.N` reference, 8 version strings.
 *
 * 🔴 EVERY RULE BELOW IS WRITTEN AGAINST LF, SO THE INPUT IS NORMALISED FIRST (s194). A trailing
 * "[ tab]* then newline" cannot match the CARRIAGE RETURN of a CRLF line ending, so rule 3
 * silently no-opped on a Windows checkout and rendered the ENTIRE internal header on the live
 * page — while CI, which clones with LF endings, stayed green. Do not remove the normalisation,
 * and do not write a new rule that re-derives line endings from the raw input.
 *
 * 🔴 RULE 3 IS CONTENT-ANCHORED, NOT LINE-COUNTED. It matches on the `*Version` / `*Drafted:`
 * openers and stops at the first `##` section heading. Every future changelog entry is appended
 * to the `*Drafted:` line, so that line only ever grows — a fixed line count or character window
 * would silently start leaking the moment it grew past the window. Do not "simplify" this to
 * "drop the first N lines".
 */

/** Matches the internal version/changelog header: a run of italic `*Version …*` / `*Drafted: …*`
 *  lines (each may wrap arbitrarily long), plus any blank lines and the `---` rule that follow,
 *  anchored to the start of the document and never crossing a `##` section heading. */
const INTERNAL_HEADER =
  /^(?:[ \t]*\*(?:Version|Drafted)[^\n]*\*[ \t]*\n+)+(?:[ \t]*---[ \t]*\n+)?/m;

export function stripInternalAnnotations(markdown: string): string {
  // 0. Normalise line endings BEFORE any rule runs. This fixes the whole fragility class, not
  //    just rule 3: rule 1 left an orphan carriage return behind every removed callout, and the
  //    3+-blank-line collapse below never fired on CRLF either. Normalising once here keeps the
  //    next rule someone adds correct by construction. Output is LF; both callers hand the result
  //    to a markdown renderer, which is line-ending agnostic.
  const source = markdown.replace(/\r\n?/g, "\n");

  // 1. Multiline flag blocks: [FLAG: ... ]
  let stripped = source.replace(/\[FLAG:[^\]]*\][ \t]*\n?/g, "");

  // 2. DRAFT status blockquote (whole paragraph starting with "> **Document status: DRAFT.**")
  stripped = stripped.replace(/^(>\s*\*\*Document status: DRAFT\.\*\*[^\n]*\n?)(>.*\n?)*/m, "");

  // 3. Internal version/changelog header. Applied to the region ABOVE the first `##` heading only,
  //    so a stray italic line inside the body can never be mistaken for header material.
  const firstSection = stripped.search(/^##\s/m);
  const head = firstSection === -1 ? stripped : stripped.slice(0, firstSection);
  const tail = firstSection === -1 ? "" : stripped.slice(firstSection);
  stripped = head.replace(INTERNAL_HEADER, "") + tail;

  // Collapse any resulting 3+ blank lines down to 2
  stripped = stripped.replace(/\n{3,}/g, "\n\n");

  return stripped.trim();
}
